import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  mimeType: string | null;
  onDownload: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
  mimeType,
  onDownload
}) => {
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [docxError, setDocxError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  // Check by MIME type AND by file extension for better compatibility
  const fileExt = fileName?.toLowerCase().split('.').pop();
  const isImage = mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(fileExt || '');
  const isPdf = mimeType === 'application/pdf' || fileExt === 'pdf';
  const isDocx = mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || fileExt === 'docx';
  const isDoc = mimeType === 'application/msword' || fileExt === 'doc';
  const isWordDoc = isDocx || isDoc;
  const canPreview = isImage || isPdf || isWordDoc;

  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setNumPages(null);
      setDocxHtml(null);
      setDocxError(null);
      setLoading(true);

      // Load DOCX content
      if (isDocx) {
        loadDocxContent();
      } else if (isDoc) {
        // .doc files are not supported by mammoth, show message
        setDocxError('.doc files are not supported for preview. Please use .docx format or download the file.');
        setLoading(false);
      }
    }
  }, [isOpen, fileUrl, mimeType]);

  const loadDocxContent = async () => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch document');
      
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocxHtml(result.value);
      setLoading(false);
    } catch (error) {
      console.error('Error loading DOCX:', error);
      setDocxError('Failed to load document preview');
      setLoading(false);
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="relative bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden flex flex-col w-[90vw] h-[85vh] max-w-6xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-800 flex-shrink-0">
            <div className="min-w-0 flex-1">
              <h3 className="text-white font-medium truncate">{fileName}</h3>
              <p className="text-gray-500 text-sm">{mimeType || 'Unknown type'}</p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              {/* Zoom controls - only for images and PDFs */}
              {(isImage || isPdf) && (
                <>
                  <button
                    onClick={handleZoomOut}
                    className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                    title="Zoom out"
                  >
                    <FiZoomOut className="w-5 h-5" />
                  </button>
                  <span className="text-gray-400 text-sm min-w-[4rem] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                    title="Zoom in"
                  >
                    <FiZoomIn className="w-5 h-5" />
                  </button>
                </>
              )}
              {/* Close button */}
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                title="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto bg-gray-950 flex items-center justify-center">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-950">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {canPreview ? (
              isImage ? (
                <div 
                  className="w-full h-full overflow-auto flex items-center justify-center p-4"
                  style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
                >
                  <img
                    src={fileUrl}
                    alt={fileName}
                    style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
                    className="max-w-full max-h-full object-contain transition-transform duration-200"
                    onLoad={() => setLoading(false)}
                    onError={() => setLoading(false)}
                  />
                </div>
              ) : isPdf ? (
                <div className="w-full h-full overflow-auto flex flex-col items-center p-4 gap-4">
                  <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={() => setLoading(false)}
                    loading={null}
                    className="flex flex-col items-center gap-4"
                  >
                    {numPages && Array.from({ length: numPages }, (_, i) => (
                      <Page
                        key={i + 1}
                        pageNumber={i + 1}
                        scale={zoom}
                        className="shadow-2xl"
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    ))}
                  </Document>
                </div>
              ) : isWordDoc ? (
                <div className="w-full h-full overflow-auto p-8">
                  {docxError ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <p className="text-gray-400 mb-4 text-center">{docxError}</p>
                      <button
                        onClick={onDownload}
                        className="px-5 py-2.5 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all flex items-center gap-2"
                      >
                        <FiDownload className="w-4 h-4" />
                        Download File
                      </button>
                    </div>
                  ) : docxHtml ? (
                    <div 
                      className="bg-white rounded-xl p-8 max-w-4xl mx-auto shadow-2xl prose prose-sm max-w-none"
                      style={{ color: '#1a1a1a' }}
                      dangerouslySetInnerHTML={{ __html: docxHtml }}
                    />
                  ) : null}
                </div>
              ) : null
            ) : (
              <div className="text-center p-8">
                <p className="text-gray-400 mb-4">
                  Preview not available for this file type
                </p>
                <button
                  onClick={onDownload}
                  className="px-5 py-2.5 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all flex items-center gap-2 mx-auto"
                >
                  <FiDownload className="w-4 h-4" />
                  Download File
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FilePreviewModal;
